import { useParams, Link } from 'react-router-dom';
import { getArticleById, getLatestArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { siteInfo } from '@/mocks/site';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : null;
  const relatedArticles = getLatestArticles(4).filter((a) => a.id !== id).slice(0, 3);

  if (!article) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4"></i>
        <h2 className="text-xl font-semibold text-foreground-700 mb-2">文章不存在</h2>
        <Link to="/" className="text-accent-500 hover:text-accent-600 transition-colors">
          返回首页
        </Link>
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
      url: siteInfo.url,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteInfo.url}/article/${article.id}`,
    },
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`${siteInfo.url}/article/${article.id}`}
        ogType="article"
        ogImage={article.imageUrl}
        articlePublishedTime={article.publishDate}
        articleModifiedTime={article.publishDate}
        schema={articleSchema}
      />

      <main>
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: article.categoryName, to: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="px-4 md:px-6 py-8 max-w-4xl mx-auto">
          {/* Header */}
          <header className="mb-8">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Link
                to={`/category/${article.category}`}
                className="text-xs px-3 py-1 rounded-full bg-accent-500/10 text-accent-500 whitespace-nowrap hover:bg-accent-500/20 transition-colors"
              >
                {article.categoryName}
              </Link>
              {article.isHot && (
                <span className="text-xs px-3 py-1 rounded-full bg-foreground-950 text-accent-500 font-medium whitespace-nowrap">
                  HOT
                </span>
              )}
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 leading-snug mb-4">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万阅读` : `${article.views}阅读`}
              </span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="mb-8 rounded-xl overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full aspect-[16/9] object-cover"
            />
          </div>

          {/* Article Body */}
          <div
            className="prose prose-lg max-w-none mb-8 text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Tags */}
          <div className="flex items-center gap-2 flex-wrap border-t border-background-200 pt-6 mb-8">
            <span className="text-sm text-foreground-500">标签：</span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs px-3 py-1 rounded-full bg-background-100 text-foreground-600 border border-background-200 whitespace-nowrap"
              >
                #{tag}
              </span>
            ))}
          </div>

          {/* Share */}
          <div className="flex items-center gap-3 border-t border-background-200 pt-6 mb-12">
            <span className="text-sm text-foreground-500">分享：</span>
            <a
              href={`https://weibo.com/share?url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(article.title)}`}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-accent-500 hover:text-background-50 transition-colors"
              title="分享到微博"
            >
              <i className="ri-weibo-line"></i>
            </a>
            <a
              href={`https://connect.qq.com/widget/shareqq/index.html?url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(article.title)}`}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-accent-500 hover:text-background-50 transition-colors"
              title="分享到QQ"
            >
              <i className="ri-qq-line"></i>
            </a>
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="border-t border-background-200 pt-8">
              <div className="flex items-center gap-2 mb-5">
                <i className="ri-links-line text-accent-500"></i>
                <h2 className="text-lg font-semibold text-foreground-900">相关爆料</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.id} article={rel} />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
    </>
  );
}